import { useState, useEffect, useCallback } from 'preact/hooks';
import {
  fetchCreditPricing,
  upsertCreditPricing,
  deleteCreditPricing,
  type CreditPricingRow,
} from '../api/admin';

// ════════════════════════════════════════════════════════════════════════════
// CreditPricingTab — admin editor for the credit_pricing matrix
// (valko-credits-platform Phase 2, F4). A flat list of (actionKind, modelClass)
// → centicredits rows. Ships EMPTY: an unpriced action leaves stage-5 dark for
// that action (peg 1 credit = €0.10 PROPOSED, operator ratifies before seeding).
// Clone of TierQuotasTab conventions (inline add, integer validation mirroring
// the backend PUT /admin/credits/pricing).
// ════════════════════════════════════════════════════════════════════════════

const PEG_NOTE = 'Pricing matrix empty — peg awaits operator ratification (docs/credits-pricing.md)';

const thStyle: Record<string, string | number> = { padding: '8px 6px', textAlign: 'left', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' };
const tdStyle: Record<string, string | number> = { padding: '8px 6px', verticalAlign: 'middle' };
const inputStyle: Record<string, string | number> = { fontSize: 12, padding: '4px 6px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)', borderRadius: 4, width: '100%', boxSizing: 'border-box' };
const addBtnStyle: Record<string, string | number> = { background: 'var(--theme-accent)', color: 'var(--text-on-accent, #fff)', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' };

export function CreditPricingTab() {
  const [rows, setRows] = useState<CreditPricingRow[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Inline add form.
  const [newAction, setNewAction] = useState('');
  const [newModel, setNewModel] = useState('');
  const [newCenti, setNewCenti] = useState('');

  const load = useCallback(() => {
    fetchCreditPricing()
      .then((r) => setRows(r.rows))
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  function validCenti(raw: string): number | null {
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : null;
  }

  async function handleAdd() {
    setError('');
    const centi = validCenti(newCenti);
    if (!newAction.trim() || !newModel.trim()) { setError('actionKind and modelClass are required'); return; }
    if (centi == null) { setError('centicredits must be a non-negative integer'); return; }
    setSaving(true);
    try {
      await upsertCreditPricing({ actionKind: newAction.trim(), modelClass: newModel.trim(), centicredits: centi });
      setNewAction(''); setNewModel(''); setNewCenti('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleEditCenti(row: CreditPricingRow, raw: string) {
    const centi = validCenti(raw);
    if (centi == null) { setError('centicredits must be a non-negative integer'); return; }
    if (centi === row.centicredits) return;
    setError('');
    try {
      await upsertCreditPricing({ actionKind: row.actionKind, modelClass: row.modelClass, centicredits: centi });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  }

  async function handleDelete(row: CreditPricingRow) {
    setError('');
    try {
      await deleteCreditPricing(row.actionKind, row.modelClass);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <div class="admin-content">
      {error && <p style={{ color: 'var(--accent-red)', fontSize: 12 }}>{error}</p>}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--text-dim)', fontSize: 13, fontStyle: 'italic', padding: '8px 0' }}>{PEG_NOTE}</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Action kind</th>
              <th style={thStyle}>Model class</th>
              <th style={thStyle}>Centicredits</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.actionKind}:${r.modelClass}`} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={tdStyle}>{r.actionKind}</td>
                <td style={tdStyle}>{r.modelClass}</td>
                <td style={tdStyle}>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    defaultValue={String(r.centicredits)}
                    style={{ ...inputStyle, width: 90 }}
                    onBlur={(e) => handleEditCenti(r, (e.target as HTMLInputElement).value)}
                  />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => handleDelete(r)}
                    style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', fontSize: 12 }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Inline add row */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <input style={{ ...inputStyle, width: 130 }} placeholder="actionKind" value={newAction} onInput={(e) => setNewAction((e.target as HTMLInputElement).value)} />
        <input style={{ ...inputStyle, width: 130 }} placeholder="modelClass" value={newModel} onInput={(e) => setNewModel((e.target as HTMLInputElement).value)} />
        <input style={{ ...inputStyle, width: 100 }} type="number" min={0} step={1} placeholder="centicredits" value={newCenti} onInput={(e) => setNewCenti((e.target as HTMLInputElement).value)} />
        <button type="button" style={addBtnStyle} disabled={saving} onClick={handleAdd}>Add / update</button>
      </div>
      <p style={{ color: 'var(--text-dim)', fontSize: 11, marginTop: 10 }}>
        1 credit = 100 centicredits (peg 1 credit = €0.10, PROPOSED). An unpriced action leaves stage-5 balance enforcement dark for it.
      </p>
    </div>
  );
}
